/**
 * InstructionsStep
 *
 * Extracted from LanguageTestModal — Step 1: Instructions display
 * with sample image and formatted instruction text.
 */

import type { TFunction } from 'i18next';
import { cn } from '@tonnie/foundation';
import { Card, CardContent } from '@tonnie/ui';
import { FileText, Image as ImageIcon } from '@tonnie/assets';

import type { ILanguageTestConfig } from './language-test.types';

// ── Instructions Text Renderer ──────────────────────────────────────────

function InstructionsText({ testConfig, t }: { testConfig: ILanguageTestConfig; t: TFunction }) {
  const nodes: React.ReactNode[] = [];

  testConfig.instructions.forEach((instruction, idx) => {
    const parts = instruction.split('**');
    parts.forEach((part, i) => {
      const isBold = i % 2 === 1;
      if (part.includes('use this extension') && testConfig.extensionUrl) {
        const before = part.substring(0, part.indexOf('use this extension'));
        const after = part.substring(
          part.indexOf('use this extension') + 'use this extension'.length
        );
        if (before)
          nodes.push(
            <span key={`${idx}-${i}-b`} className="text-sm text-foreground">
              {before}
            </span>
          );
        nodes.push(
          <span
            key={`${idx}-${i}-link`}
            onClick={() => window.open(testConfig.extensionUrl, '_blank')}
            className="text-sm underline cursor-pointer text-primary-accessible hover:text-primary-accessible/80"
          >
            {t('checkup.languageTests.useThisExtension')}
          </span>
        );
        if (after)
          nodes.push(
            <span key={`${idx}-${i}-a`} className="text-sm text-foreground">
              {after}
            </span>
          );
      } else {
        nodes.push(
          <span
            key={`${idx}-${i}`}
            className={cn('text-sm text-foreground', isBold && 'font-semibold')}
          >
            {part}
          </span>
        );
      }
    });
    if (idx < testConfig.instructions.length - 1) nodes.push(<br key={`br-${idx}`} />);
  });

  nodes.push(
    <span key="attention-block">
      <br />
      <br />
      <span className="text-sm font-semibold text-tone-amber">
        {t('checkup.languageTests.attention')}{' '}
      </span>
      <span className="text-sm text-foreground">{t('checkup.languageTests.attentionText')}</span>
      <span className="text-sm font-semibold text-foreground">
        {t('checkup.languageTests.immediatelyAfter')}
      </span>
      <span className="text-sm text-foreground">{t('checkup.languageTests.retakeWarning')}</span>
    </span>
  );

  return <div>{nodes}</div>;
}

// ── Main Component ──────────────────────────────────────────────────────

export function InstructionsStep({
  testConfig,
  t,
}: {
  testConfig: ILanguageTestConfig;
  t: TFunction;
}) {
  return (
    <div className="p-6 space-y-6">
      {/* Instructions card */}
      <Card className="border-2 border-primary/20 bg-linear-to-br from-primary/5 to-background">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="w-5 h-5 text-primary-accessible" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground mb-1">
                {t('checkup.languageTests.importantInstructions')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('checkup.languageTests.readCarefully')}
              </p>
            </div>
          </div>

          <div className="bg-background rounded-lg p-5 border border-border/50 space-y-3">
            <InstructionsText testConfig={testConfig} t={t} />
          </div>
        </CardContent>
      </Card>

      {/* Sample image */}
      {testConfig.sampleImagePath && (
        <Card className="border border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {t('checkup.languageTests.sampleResults')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t('checkup.languageTests.sampleHint')}
            </p>
            <div className="border-2 border-border rounded-lg overflow-hidden bg-muted/30">
              <img
                src={
                  testConfig.sampleImagePath.startsWith('/')
                    ? testConfig.sampleImagePath
                    : `/${testConfig.sampleImagePath}`
                }
                alt={t('checkup.languageTests.alt.sampleResults')}
                className="w-full object-contain max-h-[300px]"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
